import {useCallback, useEffect, useMemo, useRef} from 'react';

import {useStateFromStores, useStateFromStoresArray} from './useStateFromStores';

import type {Store} from './Store';
import type {ResponseError} from '@discordapp/http-utils';

type ValidateData = () => void;
type QueryId = string[];
type OptionalQueryId = QueryId | null | undefined;

export interface FetchStoreOptions<T, A extends unknown[]> {
  queryId: (...args: A) => OptionalQueryId;

  /**
   * Get data from the Flux store
   */
  get: (...args: A) => T;
  /**
   * Load data from the API. The provided AbortSignal should be used to abort the request.
   */
  load: (signal: AbortSignal, ...args: A) => Promise<void>;

  /**
   * useStateFromStores hook to use for fetching data from Flux
   */
  useStateHook: T extends any[] ? typeof useStateFromStoresArray<T> : typeof useStateFromStores<T>;

  /**
   * Whether to abort the request on effect cleanup.
   *
   * **This is dangerous.** If the abort triggers a remount of the component, you could create an infinite render loop.
   * Only use this in components where the DOM tree is stable on loading state.
   */
  dangerousAbortOnCleanup?: boolean;

  /**
   * Option to limit the number of retries after a fail. Defaults to DEFAULT_MAX_FETCH_ERRORS
   */
  maxNumFetchErrors?: number;
}

export interface FetchStoreData<T> {
  /**
   * The data available in Flux
   */
  data: T;
  /**
   * The error that occurred while fetching data
   */
  error?: Error;
  /**
   * Whether data is being loaded
   */
  isLoading: boolean;
}

export type FetchStoreHook<T, A extends unknown[]> = (...args: A) => FetchStoreData<T>;

interface DataFetchState {
  controller?: AbortController;
  error?: Error;
  fetchFailCounter: number;
  isLoading?: boolean;
  isStale?: boolean;
  validateData?: ValidateData;
}

class DataFetchStateTrie {
  #nodes: Record<string, DataFetchStateTrie> = {};
  #state: DataFetchState = {fetchFailCounter: 0};

  abort(keys: OptionalQueryId): void {
    const node = this.search(keys);
    const {controller} = node.#state;
    if (controller != null) {
      controller.abort();
    }
  }

  doesDataNeedValidation(keys: OptionalQueryId): boolean {
    const node = this.search(keys);
    return node.#state.isStale === true;
  }

  getOrCreate(key: string): DataFetchStateTrie {
    if (this.#nodes[key] == null) {
      this.#nodes[key] = new DataFetchStateTrie();
    }

    return this.#nodes[key];
  }

  getState(keys: OptionalQueryId): DataFetchState {
    return this.search(keys).#state;
  }

  loadingDone(keys: QueryId, isSuccess = false) {
    const node = this.search(keys);
    node.#state.isLoading = false;

    if (isSuccess) {
      node.#state.fetchFailCounter = 0;
      node.#state.isStale = false;
    } else {
      node.#state.fetchFailCounter += 1;
    }
  }

  loadingStart(keys: QueryId, controller?: AbortController) {
    const node = this.search(keys);
    node.#state.isLoading = true;

    if (controller != null) {
      node.#state.controller = controller;
    }

    // Reset error state whenever we start a new fetch
    node.#state.error = undefined;
  }

  search(keys: OptionalQueryId): DataFetchStateTrie {
    if (keys == null) {
      return new DataFetchStateTrie();
    }

    let node: DataFetchStateTrie = this;
    for (const key of keys) {
      node = node.getOrCreate(key);
    }

    return node;
  }

  setError(keys: QueryId, error: Error) {
    const node = this.search(keys);
    node.#state.error = error;

    // Reset stale state when the error is set
    node.#state.isStale = false;
  }

  subscribe(keys: OptionalQueryId, validateData: undefined | ValidateData): void {
    const node = this.search(keys);
    node.#state.validateData = validateData;
  }

  validate(keys: QueryId): void {
    const parentNode = this.search(keys);

    const validateCallbacks: ValidateData[] = [];
    if (typeof parentNode.#state.validateData === 'function') {
      validateCallbacks.push(parentNode.#state.validateData);
    }

    // Collect all of the validators we need to emit
    const nodesToVisit = Object.values(parentNode.#nodes);
    while (nodesToVisit.length > 0) {
      const node = nodesToVisit.pop();
      if (node != null) {
        node.#state.isStale = true;
        DataFetchStateTrie.resetErrorState(node);

        nodesToVisit.push(...Object.values(node.#nodes));

        if (typeof node.#state.validateData === 'function') {
          validateCallbacks.push(node.#state.validateData);
        }
      }
    }

    parentNode.#state.isStale = true;
    DataFetchStateTrie.resetErrorState(parentNode);

    validateCallbacks.forEach((callback) => callback());
  }

  static resetErrorState(node: DataFetchStateTrie) {
    node.#state.error = undefined;
    node.#state.fetchFailCounter = 0;
  }
}

const DataValidationCache = new DataFetchStateTrie();
const DEFAULT_MAX_FETCH_ERRORS = 5;

class HTTPResponseError extends Error {
  name = 'HTTPResponseError';

  status = 0;

  setStatus(statusCode: number) {
    this.status = statusCode;
  }
}

function makeError(e: Error | ResponseError): Error {
  if (e instanceof Error) {
    return e;
  }

  // ResponseError
  if (typeof e === 'object') {
    // ResponseErrorBody
    if ('body' in e && e.body != null && 'message' in e.body) {
      const err = new HTTPResponseError(String(e.body.message));
      err.setStatus(e.status);
      return err;
    }

    // OldFormBodyError
    const msg = Object.entries(e).map((key, value) => {
      return `${key}: [${String(value)}]`;
    });
    const err = new HTTPResponseError(msg.join(','));
    err.setStatus(e.status);
    return err;
  }

  // Fallback, should never hit this case
  return new Error(String(e));
}

/**
 * Create a hook that uses data from Flux if available, otherwise loads it. The Flux
 * store is responsible for caching the data itself and the loading state. Errors
 * are tracked in local state.
 *
 * Types should be explicitly provided to this function.
 * - T represents the data stored in Flux
 * - A _must_ be a tuple: it represents the type and number of arguments provided to
 *   options and as hook dependencies. It is illegal to provide a variable number of
 *   dependencies to hooks, and leaving A inferred as unknown[] is very unsafe.
 *
 * @returns A React hook that accepts A-arguments and returns information about T-data
 */
export function createFetchStore<T, A extends unknown[]>(
  store: Array<Store<any>> | Store<any>,
  {
    dangerousAbortOnCleanup = false,
    get,
    load,
    maxNumFetchErrors = DEFAULT_MAX_FETCH_ERRORS,
    queryId,
    useStateHook,
  }: FetchStoreOptions<T, A>,
): FetchStoreHook<T, A> {
  return (...args: A) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const id = useMemo(() => queryId(...args), args);

    const stores = Array.isArray(store) ? store : [store];

    const data = useStateHook(stores, () => get(...args), args);

    const dataFetchState = DataValidationCache.getState(id);
    const error = dataFetchState.error;
    const isStoreCacheLoading = dataFetchState.isLoading === true;

    // argsRef used in validateData to avoid dependencies with volatile args
    const argsRef = useRef(args);

    useEffect(() => {
      // Updating argsRef.current on every run so that we always have the latest
      // and freshest args.
      argsRef.current = args;
    }, [args]);

    const shouldLoad = useCallback(() => {
      if (id == null) {
        return false;
      }

      if (isStoreCacheLoading) {
        return false;
      }

      // An empty array is considered as data not loaded if we're using the array
      // version of the hook. Otherwise we do a null/undefined check.
      let isDataHydrated = false;
      if (useStateHook === useStateFromStoresArray) {
        const dataArray = data as any[];
        if (dataArray.length > 0) {
          isDataHydrated = true;
        }
      } else if (data != null) {
        isDataHydrated = true;
      }

      const isDataStale = DataValidationCache.doesDataNeedValidation(id);
      const hasFetchingError = error != null;

      // Check if we need to load data:
      // 1. If data is stale, we need to reload regardless of hydration state
      // 2. If data is not hydrated and we don't have an error, we need to load
      return isDataStale || (!isDataHydrated && !hasFetchingError);
    }, [data, error, id, isStoreCacheLoading]);

    const validateData = useCallback(() => {
      if (id == null) {
        return;
      }

      if (!shouldLoad()) {
        return;
      }

      const controller = new AbortController();

      DataValidationCache.loadingStart(id, dangerousAbortOnCleanup ? controller : undefined);

      load(controller.signal, ...argsRef.current)
        .then((res) => {
          DataValidationCache.loadingDone(id, true);
          return res;
        })
        .catch((e: Error | ResponseError) => {
          DataValidationCache.loadingDone(id);

          if (controller.signal.aborted) {
            return;
          }

          const err = makeError(e);

          const hasReachedMaxFetches = dataFetchState.fetchFailCounter >= maxNumFetchErrors;

          // API returns a 5xx or 429 we won't set the error result so that we can retry the request
          if (!hasReachedMaxFetches && err instanceof HTTPResponseError && (err.status >= 500 || err.status === 429)) {
            return;
          }

          DataValidationCache.setError(id, err);
        });
    }, [dataFetchState.fetchFailCounter, id, shouldLoad]);

    useEffect(() => {
      validateData();
      DataValidationCache.subscribe(id, validateData);

      return () => {
        DataValidationCache.abort(id);
        DataValidationCache.subscribe(id, undefined);
      };
    }, [id, validateData]);

    // Respect what the trie says first, otherwise call our shouldLoad function
    const isLoading = isStoreCacheLoading || shouldLoad();

    return {data, error, isLoading};
  };
}

export function revalidateFetchStoreData(keys: string[]): void {
  DataValidationCache.validate(keys);
}
